import React, { useCallback, useMemo, useState } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, TextInput, View } from 'react-native';
import { Image } from 'expo-image';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Switch from '@/components/Inputs/Switch';
import Button from '@/components/Buttons/Button';
import { PLUS_COLORS } from '@/constants/Colors';

// Desktop / tablet (`!isXS`) implementation of `ModelSelectorDetailed`.
// Mirrors the V6 design from the lab — narrow always-visible left rail
// of categories, MiniCard grid on the right, sticky color-coded section
// headers in the "All" view, multi-select toggle + selected chips +
// Done button in the footer. The XS / mobile path keeps the original
// list-style UI inside `ModelSelectorDetailed` itself; this component
// is only mounted when the viewport is wide enough for the rail to fit
// without crowding the model cards.
//
// All data fetching, filter resolution, Plus-modal triggering, and the
// `onChange` contract live in the parent. This file is intentionally a
// view layer over those — keeps Plus gating, capability filters, and
// `noAutoMode` / `restrictMultipleModelsToSameType` behavior identical
// across the two viewports.

// Selection blue. Kept off the per-theme accent on purpose so selected
// state doesn't shift hue when the user changes themes.
const SELECTION_BLUE = '#3B82F6';
const RAIL_WIDTH = 176;

// Hashed-from-slug palette so every tag has a stable color across reloads.
// Same family as the rest of the app's accent set — no surprise hues.
const TAG_PALETTE = [
  '#3B82F6', '#22C55E', '#EC4899', '#8B5CF6', '#F59E0B',
  '#06B6D4', '#A855F7', '#14B8A6', '#0EA5E9', '#EF4444',
  '#EAB308', '#10B981',
];

const colorForSlug = (slug) => {
  if (!slug) return TAG_PALETTE[0];
  let hash = 0;
  for (let i = 0; i < slug.length; i++) hash = (hash * 31 + slug.charCodeAt(i)) >>> 0;
  return TAG_PALETTE[hash % TAG_PALETTE.length];
};

// Number of models surfaced in the "Newest" section that pins to the top
// of the All view. Kept small on purpose so it stays a curated showcase
// instead of becoming a second full list — the per-tag sections below
// still own the long tail.
const NEWEST_LIMIT = 3;

// Curated category order in the left rail (below Favorites) and in the
// All-view scroll sections (after Newest). Unlisted tags follow alphabetically.
const TAG_CATEGORY_ORDER = [
  'text-generation',
  'image-generators',
  'image-editing',
  'video-generators',
  'audio-generators',
];

const compareByCategoryOrder = (slugA, slugB, labelA = '', labelB = '') => {
  const orderIndex = new Map(TAG_CATEGORY_ORDER.map((slug, i) => [slug, i]));
  const aIdx = orderIndex.has(slugA) ? orderIndex.get(slugA) : Infinity;
  const bIdx = orderIndex.has(slugB) ? orderIndex.get(slugB) : Infinity;
  if (aIdx !== bIdx) return aIdx - bIdx;
  return String(labelA).localeCompare(String(labelB));
};

// Build the "Newest" pseudo-section — the most recently added models from
// the full (capability-filtered) catalog. We sort by `createdAt` desc and
// cap at NEWEST_LIMIT. Returns null when there are no models with a
// `createdAt` value so the section gracefully disappears for older data.
const buildNewestSection = (models, label) => {
  const dated = models.filter(m => m?.createdAt);
  if (dated.length === 0) return null;
  const sorted = [...dated].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  const top = sorted.slice(0, NEWEST_LIMIT);
  if (top.length === 0) return null;
  return {
    id: '__newest__',
    label,
    icon: 'sparkles',
    color: '#FF3B30',
    models: top,
  };
};

// Group models by primary (first) tag for the "All" rail's section view.
// Mirrors V2's `groupByOutput` shape so the renderer can stay dumb.
// Anything without tags drops into an "Other" bucket pinned to the end.
const groupModelsByTag = (models, modelTags, otherLabel) => {
  const groups = new Map();
  const other = [];
  models.forEach(m => {
    const primary = Array.isArray(m.tags) && m.tags.length > 0 ? m.tags[0] : null;
    if (!primary) { other.push(m); return; }
    const slug = primary.slug;
    if (!groups.has(slug)) {
      const tag = (modelTags || {})[slug];
      groups.set(slug, {
        id: slug,
        label: tag?.name || primary.name || slug,
        icon: tag?.icon || primary.icon || 'tag',
        color: colorForSlug(slug),
        models: [],
      });
    }
    groups.get(slug).models.push(m);
  });
  const list = Array.from(groups.values()).sort((a, b) =>
    String(a?.label ?? '').localeCompare(String(b?.label ?? '')),
  );
  if (other.length > 0) {
    list.push({ id: '__other__', label: otherLabel, icon: 'circle', color: '#94A3B8', models: other });
  }
  return list;
};

// Sort tag sections: Text → Image → Image Editing → Video → Audio, then
// alpha; "Other" stays pinned to the end.
const orderTagSections = (sections) => {
  const other = sections.find(s => s.id === '__other__');
  const sorted = sections
    .filter(s => s.id !== '__other__')
    .sort((a, b) => compareByCategoryOrder(a.id, b.id, a.label, b.label));
  return other ? [...sorted, other] : sorted;
};

const isModelNew = (model) => {
  if (!model?.createdAt) return false;
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  return new Date(model.createdAt) > sevenDaysAgo;
};

// Mirror of the parent's Plus gating so the card can render the PLUS
// badge inline. The actual Pricing modal is still triggered by the
// parent's `onToggleModel` — this is purely visual.
const isModelActuallyPlusOnly = (model, userIsPlus) => {
  if (userIsPlus) return false;
  const isPlusOnly = !!model?.flags?.isPlusOnly;
  const isVideoModel = !!model?.capabilities?.output?.hasVideo;
  const isImageOnly = !!model?.capabilities?.output?.hasImages && !isVideoModel;
  // Free users can use image-only models; video/other gated need Plus or paid packs
  if (isImageOnly) return false;
  return !!(isPlusOnly || isVideoModel);
};

// ---------- MiniCard ----------------------------------------------------
const MiniCard = ({ model, isSelected, isDisabled, onPress, onInfoClick, colors, t, userIsPlus, width }) => {
  const [isHovered, setIsHovered] = useState(false);
  const cost = model?.creditCosts?.baseCost;
  const isNew = isModelNew(model);
  const isPlus = isModelActuallyPlusOnly(model, userIsPlus);

  return (
    <Pressable
      onPress={() => onPress(model)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      disabled={isDisabled}
      style={{
        width,
        backgroundColor: isSelected
          ? `${SELECTION_BLUE}14`
          : (isHovered && !isDisabled ? colors.solid_color_tertiary : 'transparent'),
        borderRadius: 10,
        borderWidth: 1.5,
        borderColor: isSelected ? SELECTION_BLUE : colors.border_color_secondary,
        padding: 10,
        gap: 6,
        position: 'relative',
        opacity: isDisabled ? 0.4 : 1,
        ...(Platform.OS === 'web' && { cursor: isDisabled ? 'not-allowed' : 'pointer' }),
      }}
    >
      {/* Top-right corner is shared by selection check, NEW badge, and
          PLUS badge. Selection wins because it's the most actionable
          piece of state; PLUS wins over NEW because Plus-gating affects
          whether a click is even allowed. */}
      {isSelected ? (
        <View
          style={{
            position: 'absolute', top: 6, right: 6, zIndex: 2,
            width: 18, height: 18, borderRadius: 9,
            backgroundColor: SELECTION_BLUE,
            alignItems: 'center', justifyContent: 'center',
          }}
        >
          <Icon name="check" size={9} color="#fff" />
        </View>
      ) : isPlus ? (
        <View
          style={{
            position: 'absolute', top: 6, right: 6, zIndex: 1,
            ...(Platform.OS === 'web' && { backgroundImage: PLUS_COLORS.backgroundImage }),
            backgroundColor: PLUS_COLORS.iconColor,
            paddingHorizontal: 5, height: 16, borderRadius: 4,
            flexDirection: 'row', alignItems: 'center', gap: 2,
          }}
        >
          <Icon name="sparkles" solid size={7} color={PLUS_COLORS.iconColor} />
          <Text style={{ color: PLUS_COLORS.textColor, textShadow: PLUS_COLORS.textShadow }} size="xxs" weight="extraBold">PLUS</Text>
        </View>
      ) : isNew ? (
        <View
          style={{
            position: 'absolute', top: 6, right: 6, zIndex: 1,
            backgroundColor: '#FF3B30',
            paddingHorizontal: 4, paddingVertical: 1, borderRadius: 4,
          }}
        >
          <Text size="xxs" color="white" weight="bold">{t('nav.modelSelectorDetailed.newBadge')}</Text>
        </View>
      ) : null}

      <View
        style={{
          width: 36, height: 36, borderRadius: 8,
          alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
          backgroundColor: model.logoPath ? '#fff' : colors.solid_color_tertiary,
        }}
      >
        {model.logoPath ? (
          <Image source={{ uri: model.logoPath }} style={{ width: 36, height: 36 }} contentFit="cover" />
        ) : (
          <Icon name="sparkles" size={14} color={colors.text_color_tertiary} />
        )}
      </View>

      <Text size="s" weight="semibold" color="primary" numberOfLines={2}>{model.name}</Text>
      
      {/* hide description for now to save space */}
      {/* <Text size="xxs" color="tertiary" numberOfLines={3} style={{ minHeight: 24, lineHeight: 12 }}>
        {model.description || ' '}
      </Text> */}

      {cost != null && cost > 0 && !model?.capabilities?.output?.hasVideo && (
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 3, marginTop: 'auto' }}>
          <Icon name="coin" solid size={10} color={colors.text_color_tertiary} />
          <Text size="xs" color="tertiary">{cost}</Text>
        </View>
      )}

      {/* Bottom-right info button — opens the inline details panel (same
          one the mobile list uses) without toggling selection. */}
      {onInfoClick && (
        <Pressable
          onPress={(e) => { e?.stopPropagation?.(); onInfoClick(model); }}
          hitSlop={6}
          accessibilityRole="button"
          accessibilityLabel={t('nav.modelSelectorDetailed.moreInfo')}
          style={{
            position: 'absolute', bottom: 6, right: 6, zIndex: 2,
            padding: 3, borderRadius: 6,
            opacity: isHovered ? 1 : 0.55,
            ...(Platform.OS === 'web' && { cursor: 'pointer' }),
          }}
        >
          <Icon name="circle-info" size={13} color={colors.text_color_tertiary} />
        </Pressable>
      )}
    </Pressable>
  );
};

// ---------- RailItem ---------------------------------------------------
const RailItem = ({ item, isActive, count, onPress, colors }) => {
  const [isHovered, setIsHovered] = useState(false);
  const accent = item.color;
  return (
    <Pressable
      onPress={() => onPress(item.id)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        flexDirection: 'row', alignItems: 'center', gap: 10,
        height: 36, paddingLeft: 12, paddingRight: 10,
        marginHorizontal: 6, marginVertical: 2,
        borderRadius: 8,
        backgroundColor: isActive
          ? `${accent}1F`
          : (isHovered ? colors.solid_color_primary : 'transparent'),
        ...(Platform.OS === 'web' && { cursor: 'pointer', transition: 'background-color 100ms ease' }),
      }}
    >
      <Icon name={item.icon || 'tag'} size={13} color={isActive ? accent : colors.text_color_secondary} />
      <Text
        size="xs"
        weight={isActive ? 'bold' : 'semibold'}
        numberOfLines={1}
        style={{ flex: 1, color: isActive ? accent : colors.text_color_primary }}
      >
        {item.label}
      </Text>
      {count > 0 && (
        <Text
          size="xs"
          weight="medium"
          numberOfLines={1}
          style={{
            color: isActive ? accent : colors.text_color_secondary,
            fontVariant: ['tabular-nums'],
          }}
        >
          {count > 99 ? '99+' : count}
        </Text>
      )}
    </Pressable>
  );
};

// ---------- SectionHeader (sticky on web) ------------------------------
const SectionHeader = ({ section, count, isCollapsed, onToggle, colors }) => (
  <Pressable
    onPress={onToggle}
    style={{
      flexDirection: 'row', alignItems: 'center', gap: 8,
      paddingHorizontal: 12, paddingVertical: 8,
      backgroundColor: colors.solid_color_primary,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: colors.border_color_secondary,
      ...(Platform.OS === 'web' && {
        cursor: 'pointer',
        position: 'sticky', top: 0, zIndex: 1,
      }),
    }}
  >
    <View
      style={{
        width: 24, height: 24, borderRadius: 6,
        alignItems: 'center', justifyContent: 'center',
        backgroundColor: `${section.color}1F`,
      }}
    >
      <Icon name={section.icon} size={12} color={section.color} />
    </View>
    <Text size="s" weight="bold" color="primary" style={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
      {section.label}
    </Text>
    <View
      style={{
        paddingHorizontal: 7, height: 18, borderRadius: 9,
        backgroundColor: colors.solid_color_tertiary,
        alignItems: 'center', justifyContent: 'center',
      }}
    >
      <Text size="xs" color="secondary" weight="semibold">{count}</Text>
    </View>
    <View style={{ flex: 1 }} />
    <Icon name={isCollapsed ? 'chevron-right' : 'chevron-down'} size={11} color={colors.text_color_tertiary} />
  </Pressable>
);

// ---------- Main desktop component -------------------------------------
const ModelSelectorDetailedDesktop = ({
  // data
  searchCatalog,            // array — capability-filtered, used when search is active
  fullCatalog,              // array — capability-filtered, all models (no rail/search filter)
  modelTags,                // object — slug → tag
  frequentModelIds,         // array
  favoriteModelIds,         // array

  // search
  searchQuery,
  onSearchChange,
  onClearSearch,
  isSearching,
  searchError,

  // selection
  selectedModelIds,
  selectedModels,
  effectiveMaxModels,
  isMultiSelectEnabled,

  // handlers (parent owns Plus-modal, capability checks, onChange contract)
  onToggleModel,
  onRemoveModel,
  onMultiSelectToggle,
  onAuto,
  onRequestClose,
  onInfoClick,

  // mode flags (passthrough from parent props)
  noAutoMode,
  noMultiSelectMode,
  showDoneButton,
  showSelectedModels,
  restrictMultipleModelsToSameType,
  isSameModelType,

  // user / loading
  userIsPlus,
  canUseGatedModels = userIsPlus,
  isLoadingAllModels,
  isFetchedAllModels,

  // misc
  searchInputRef,
  maxModels,
}) => {
  const { t } = useTranslation();
  const { colors } = useTheme();

  const [activeRail, setActiveRail] = useState('all');
  const [collapsedSections, setCollapsedSections] = useState({});

  const toggleSectionCollapsed = useCallback((id) => {
    setCollapsedSections(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);

  // Three pinned items at the top of the rail. Each gets its own brand
  // color so the rail still reads as 3-color even before the dynamic
  // tag list lands.
  const pinnedItems = useMemo(() => [
    { id: 'all',      label: t('nav.modelSelectorDetailed.all'),       icon: 'grid-2',           color: '#3B82F6' },
    { id: 'frequent', label: t('nav.modelSelectorDetailed.frequent'),  icon: 'clock-rotate-left', color: '#F59E0B' },
    { id: 'favorite', label: t('nav.modelSelectorDetailed.favorites'), icon: 'star',              color: '#EC4899' },
  ], [t]);

  // Build the full rail. Pinned items first, then every tag in alpha
  // order. We hide pinned items that have zero matches under the active
  // capability filter (e.g. a video tool shouldn't show a "Frequent"
  // entry if none of the user's frequent models can do video).
  const rail = useMemo(() => {
    // Be defensive about the tag shape — tags coming from the API can land
    // without a `name` (or even without a `slug`) during partial loads, and
    // a single undefined label here used to blow up `localeCompare` and crash
    // the whole modal (e.g. when opened from a deck column).
    const tags = Object.values(modelTags || {})
      .filter(tag => tag && tag.slug)
      .map(tag => ({
        id: `tag:${tag.slug}`,
        label: tag.name || tag.slug,
        icon: tag.icon || 'tag',
        tagSlug: tag.slug,
        color: colorForSlug(tag.slug),
      }));
    tags.sort((a, b) => compareByCategoryOrder(a.tagSlug, b.tagSlug, a.label, b.label));
    return [...pinnedItems, ...tags];
  }, [modelTags, pinnedItems]);

  const railCounts = useMemo(() => {
    const counts = {
      all: fullCatalog.length,
      frequent: fullCatalog.filter(m => frequentModelIds.includes(m.id)).length,
      favorite: fullCatalog.filter(m => favoriteModelIds.includes(m.id)).length,
    };
    Object.values(modelTags || {}).forEach(tag => {
      counts[`tag:${tag.slug}`] = fullCatalog.filter(m =>
        Array.isArray(m.tags) && m.tags.some(mt => mt.slug === tag.slug || mt.id === tag.id)
      ).length;
    });
    return counts;
  }, [fullCatalog, modelTags, frequentModelIds, favoriteModelIds]);

  // Filter for the right column based on the active rail item. When the
  // user is searching, ignore the rail and show search results — the
  // search box already crosses every category.
  const activeQuery = (searchQuery || '').trim();
  const filteredModels = useMemo(() => {
    if (activeQuery) return searchCatalog;
    if (activeRail === 'all') return fullCatalog;
    if (activeRail === 'frequent') {
      return frequentModelIds
        .map(id => fullCatalog.find(m => m.id === id))
        .filter(Boolean);
    }
    if (activeRail === 'favorite') {
      const set = new Set(favoriteModelIds);
      return fullCatalog.filter(m => set.has(m.id));
    }
    if (activeRail.startsWith('tag:')) {
      const slug = activeRail.slice(4);
      return fullCatalog.filter(m => Array.isArray(m.tags) && m.tags.some(t => t.slug === slug));
    }
    return fullCatalog;
  }, [activeQuery, searchCatalog, fullCatalog, activeRail, frequentModelIds, favoriteModelIds]);

  const activeRailItem = rail.find(r => r.id === activeRail);

  // Group only when on All AND not searching — every other rail item is
  // already a single category, and search is its own one-list view.
  const isGrouped = activeRail === 'all' && !activeQuery;
  const groupedSections = useMemo(() => {
    if (!isGrouped) return [];
    const tagSections = orderTagSections(
      groupModelsByTag(filteredModels, modelTags, t('nav.modelSelectorDetailed.other')),
    );
    // Pin "Newest" to the very top, then Text / Image / Image Editing /
    // Video / Audio, then the rest of the per-tag long tail as the user scrolls.
    const newest = buildNewestSection(filteredModels, t('nav.modelSelectorDetailed.newest'));
    return newest ? [newest, ...tagSections] : tagSections;
  }, [isGrouped, filteredModels, modelTags, t]);

  // Card width: 50% minus half the row gap, so two fit per row exactly.
  const cardWidth = Platform.OS === 'web' ? 'calc(32.5% - 4px)' : '32.5%';

  // Newest catalog additions, shown as suggestions when a search comes up
  // empty. `fullCatalog` is already capability-filtered by the parent, so
  // suggestions stay valid for the current picker context.
  const latestModels = useMemo(() => {
    const dated = fullCatalog.filter(m => m?.createdAt);
    return [...dated]
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .slice(0, 6);
  }, [fullCatalog]);

  // Compatibility disable, mirrors the original list-mode behavior:
  // when restricting to same type and multi-select is on, models that
  // don't share I/O with the first selected model can't be added.
  const isCardDisabled = useCallback((model) => {
    if (!restrictMultipleModelsToSameType) return false;
    if (!isMultiSelectEnabled) return false;
    if (selectedModels.length === 0) return false;
    if (selectedModelIds.includes(model.id)) return false;
    return !isSameModelType?.(selectedModels[0], model);
  }, [restrictMultipleModelsToSameType, isMultiSelectEnabled, selectedModels, selectedModelIds, isSameModelType]);

  const isAuto = !selectedModelIds || selectedModelIds.length === 0;
  const isDoneVisible = showDoneButton && isMultiSelectEnabled && effectiveMaxModels > 1;
  const isSelectedFooterVisible = showSelectedModels && isMultiSelectEnabled && selectedModels.length > 0;

  const styles = useMemo(() => StyleSheet.create({
    root: { flex: 1, flexBasis: 0, minHeight: 0, backgroundColor: colors.solid_color_primary, borderRadius: 18, overflow: 'hidden' },
    header: {
      paddingHorizontal: 14, paddingTop: 14, paddingBottom: 10, gap: 10,
      borderBottomWidth: 1, borderBottomColor: colors.border_color_secondary,
    },
    title: { fontSize: 17, fontWeight: '700', color: colors.text_color_primary },
    sub:   { fontSize: 13, color: colors.text_color_secondary, marginTop: 2 },
    searchRow: {
      position: 'relative',
      backgroundColor: colors.solid_color_tertiary,
      borderRadius: 10, paddingHorizontal: 12, height: 38,
      flexDirection: 'row', alignItems: 'center', gap: 8,
    },
    searchInput: { flex: 1, fontSize: 14, color: colors.text_color_primary, outlineStyle: 'none' },
    multiBar: {
      flexDirection: 'row', alignItems: 'center', gap: 6,
      paddingHorizontal: 14, paddingVertical: 8,
      borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: colors.border_color_secondary,
    },
    plusOnlyContainer: {
      flexDirection: 'row', alignItems: 'center', gap: 4,
      ...(Platform.OS === 'web' && { backgroundImage: PLUS_COLORS.backgroundImage }),
      height: 18, paddingHorizontal: 6, paddingVertical: 1, borderRadius: 4,
    },
    plusOnlyText: { textShadow: PLUS_COLORS.textShadow, color: PLUS_COLORS.textColor },
    plusOnlyIcon: { marginRight: 1, color: PLUS_COLORS.iconColor },
    body: { flex: 1, flexDirection: 'row', minHeight: 0 },
    rail: {
      width: RAIL_WIDTH,
      maxWidth: RAIL_WIDTH,
      backgroundColor: colors.solid_color_tertiary,
      borderRightWidth: StyleSheet.hairlineWidth, borderRightColor: colors.border_color_secondary,
    },
    listColumn: { flex: 1, minWidth: 0 },
    listHeader: {
      flexDirection: 'row', alignItems: 'center', gap: 6,
      paddingHorizontal: 12, paddingTop: 10, paddingBottom: 6,
    },
    listHeaderLabel: {
      fontSize: 12, fontWeight: '700', color: colors.text_color_secondary,
      textTransform: 'uppercase', letterSpacing: 0.5,
    },
    autoRow: {
      flexDirection: 'row', alignItems: 'center', gap: 10,
      paddingVertical: 9, paddingHorizontal: 10,
      marginHorizontal: 8, borderRadius: 8,
      marginTop: 8,
      backgroundColor: isAuto ? `${SELECTION_BLUE}14` : 'transparent',
      borderWidth: 1, borderColor: isAuto ? SELECTION_BLUE : colors.border_color_secondary, borderStyle: 'dashed',
      marginBottom: 8,
      ...(Platform.OS === 'web' && { cursor: 'pointer' }),
    },
    grid: {
      flexDirection: 'row', flexWrap: 'wrap',
      gap: 8, paddingHorizontal: 8,
    },
    empty: { padding: 24, alignItems: 'center', gap: 6 },
    suggestionsHeader: {
      flexDirection: 'row', alignItems: 'center', gap: 12,
      paddingHorizontal: 12, marginBottom: 8,
    },
    selectedContainer: {
      paddingHorizontal: 12, paddingTop: 10, paddingBottom: 10,
      borderTopWidth: 1, borderTopColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_primary,
    },
    selectedTitle: {
      flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
      paddingHorizontal: 4, marginBottom: 8,
    },
    selectedList: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
    selectedChip: {
      flexDirection: 'row', alignItems: 'center', gap: 6,
      backgroundColor: colors.solid_color_tertiary,
      borderRadius: 8, paddingVertical: 5, paddingHorizontal: 10, height: 26,
      ...(Platform.OS === 'web' && { cursor: 'pointer' }),
    },
    doneContainer: {
      paddingHorizontal: 12, paddingTop: 8, paddingBottom: 10,
      borderTopWidth: 1, borderTopColor: colors.border_color_secondary,
    },
    statusContainer: { paddingVertical: 16, alignItems: 'center' },
  }), [colors, isAuto]);

  return (
    <View style={styles.root}>
      <View style={styles.header}>
        <View>
          <Text style={styles.title}>{t('nav.modelSelectorDetailed.browseTitle')}</Text>
          <Text style={styles.sub}>{t('nav.modelSelectorDetailed.browseSubtitle')}</Text>
        </View>
        <View style={styles.searchRow}>
          <Icon name="search" size={13} color={colors.text_color_secondary} />
          <TextInput
            ref={searchInputRef}
            style={styles.searchInput}
            placeholder={t('nav.modelSelectorDetailed.search')}
            placeholderTextColor={colors.text_color_tertiary}
            value={searchQuery}
            onChangeText={onSearchChange}
          />
          {!!searchQuery && (
            <Pressable onPress={onClearSearch}>
              <Icon name="xmark" size={13} color={colors.text_color_tertiary} />
            </Pressable>
          )}
        </View>
      </View>

      {!noMultiSelectMode && (
        <View style={styles.multiBar}>
          <Text color="secondary" size="s">{t('nav.modelSelectorDetailed.multiSelect')}</Text>
          <Text color="secondary" size="s" style={{ marginLeft: 4, marginRight: 'auto' }}>
            · {t('nav.modelSelectorDetailed.compareUpTo').replace('{max}', String(maxModels))}
          </Text>
          {!userIsPlus && (
            <View style={styles.plusOnlyContainer}>
              <Icon name="sparkles" solid size={8} color={PLUS_COLORS.iconColor} style={styles.plusOnlyIcon} />
              <Text color={PLUS_COLORS.textColor} size="xxs" weight="extraBold" style={styles.plusOnlyText}>PLUS</Text>
            </View>
          )}
          <Switch value={isMultiSelectEnabled} onValueChange={onMultiSelectToggle} />
        </View>
      )}

      <View style={styles.body}>
        <ScrollView
          style={styles.rail}
          contentContainerStyle={{ paddingTop: 6, paddingBottom: 12 }}
          showsVerticalScrollIndicator={false}
          bounces={false}
          overScrollMode="never"
        >
          {rail.map((item, i) => (
            <React.Fragment key={item.id}>
              {i === pinnedItems.length && (
                <View
                  style={{
                    height: StyleSheet.hairlineWidth,
                    backgroundColor: colors.border_color_secondary,
                    marginVertical: 6, marginHorizontal: 12,
                  }}
                />
              )}
              <RailItem
                item={item}
                isActive={activeRail === item.id}
                count={railCounts[item.id] || 0}
                onPress={setActiveRail}
                colors={colors}
              />
            </React.Fragment>
          ))}
        </ScrollView>

        <View style={styles.listColumn}>
          {!isGrouped && (
            <View style={styles.listHeader}>
              <Icon name={activeRailItem?.icon || 'grid-2'} size={10} color={colors.text_color_secondary} />
              <Text style={styles.listHeaderLabel}>{activeRailItem?.label || t('nav.modelSelectorDetailed.all')}</Text>
              <View style={{ flex: 1 }} />
              <Text size="xxs" color="tertiary">{filteredModels.length}</Text>
            </View>
          )}

          <ScrollView
            style={{ flex: 1 }}
            contentContainerStyle={{ paddingBottom: 16 }}
            bounces={false}
            overScrollMode="never"
            keyboardShouldPersistTaps="handled"
          >
            {/* Auto only shows on the All rail (and only when allowed). */}
            {!noAutoMode && activeRail === 'all' && !activeQuery && (
              <Pressable onPress={onAuto} style={styles.autoRow}>
                <View
                  style={{
                    width: 14, height: 14, borderRadius: 7,
                    alignItems: 'center', justifyContent: 'center',
                    borderWidth: 1.5,
                    borderColor: isAuto ? SELECTION_BLUE : colors.border_color_secondary,
                  }}
                >
                  {isAuto && <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: SELECTION_BLUE }} />}
                </View>
                <View
                  style={{
                    width: 26, height: 26, borderRadius: 5,
                    alignItems: 'center', justifyContent: 'center',
                    backgroundColor: `${SELECTION_BLUE}1F`,
                  }}
                >
                  <Icon name="wand-magic-sparkles" size={12} color={SELECTION_BLUE} />
                </View>
                <View style={{ flex: 1 }}>
                  <Text size="n" color="primary" weight="semibold">{t('nav.modelSelectorDetailed.autoSelect')}</Text>
                  <Text size="xs" color="tertiary" numberOfLines={1}>{t('nav.modelSelectorDetailed.autoSelectDesc')}</Text>
                </View>
              </Pressable>
            )}

            {isSearching ? (
              <View style={styles.statusContainer}>
                <Text color="secondary">{t('nav.modelSelectorDetailed.searching')}</Text>
              </View>
            ) : searchError ? (
              <View style={styles.statusContainer}>
                <Text color="danger">{t('nav.modelSelectorDetailed.searchError')}</Text>
              </View>
            ) : filteredModels.length === 0 ? (
              <View>
                <View style={styles.empty}>
                  <Icon name="ghost" size={20} color={colors.text_color_tertiary} />
                  <Text size="xs" color="tertiary">
                    {activeQuery
                      ? t('nav.modelSelectorDetailed.noModels')
                      : t('nav.modelSelectorDetailed.noResultsInCategory')}
                  </Text>
                </View>
                {activeQuery && latestModels.length > 0 && (
                  <View>
                    <View style={styles.suggestionsHeader}>
                      <Text size="xs" weight="semibold" color="tertiary" style={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {t('nav.modelSelectorDetailed.latestModels')}
                      </Text>
                      <View style={{ flex: 1, height: 1, backgroundColor: colors.border_color_secondary }} />
                    </View>
                    <View style={[styles.grid, { paddingTop: 4 }]}>
                      {latestModels.map(m => (
                        <MiniCard
                          key={`latest-${m.id}`}
                          model={m}
                          isSelected={selectedModelIds.includes(m.id)}
                          isDisabled={isCardDisabled(m)}
                          onPress={onToggleModel}
                          onInfoClick={onInfoClick}
                          colors={colors}
                          t={t}
                          userIsPlus={canUseGatedModels}
                          width={cardWidth}
                        />
                      ))}
                    </View>
                  </View>
                )}
              </View>
            ) : isGrouped ? (
              groupedSections.map(section => {
                const isCollapsed = !!collapsedSections[section.id];
                return (
                  <View key={section.id}>
                    <SectionHeader
                      section={section}
                      count={section.models.length}
                      isCollapsed={isCollapsed}
                      onToggle={() => toggleSectionCollapsed(section.id)}
                      colors={colors}
                    />
                    {!isCollapsed && (
                      <View style={[styles.grid, { paddingTop: 8, paddingBottom: 12 }]}>
                        {section.models.map(m => {
                          if (showSelectedModels && selectedModelIds.includes(m.id)) return null;
                          return (
                            <MiniCard
                              key={`${section.id}-${m.id}`}
                              model={m}
                              isSelected={selectedModelIds.includes(m.id)}
                              isDisabled={isCardDisabled(m)}
                              onPress={onToggleModel}
                              onInfoClick={onInfoClick}
                              colors={colors}
                              t={t}
                              userIsPlus={canUseGatedModels}
                              width={cardWidth}
                            />
                          );
                        })}
                      </View>
                    )}
                  </View>
                );
              })
            ) : (
              <View style={styles.grid}>
                {filteredModels.map(m => {
                  if (showSelectedModels && selectedModelIds.includes(m.id)) return null;
                  return (
                    <MiniCard
                      key={m.id}
                      model={m}
                      isSelected={selectedModelIds.includes(m.id)}
                      isDisabled={isCardDisabled(m)}
                      onPress={onToggleModel}
                      onInfoClick={onInfoClick}
                      colors={colors}
                      t={t}
                      userIsPlus={canUseGatedModels}
                      width={cardWidth}
                    />
                  );
                })}
              </View>
            )}

            {!activeQuery && activeRail === 'all' && !isFetchedAllModels && isLoadingAllModels && (
              <View style={styles.statusContainer}>
                <Text color="secondary">{t('nav.modelSelectorDetailed.loadingMore')}</Text>
              </View>
            )}
          </ScrollView>
        </View>
      </View>

      {isSelectedFooterVisible && (
        <View style={styles.selectedContainer}>
          <View style={styles.selectedTitle}>
            <Text color="secondary" size="xs">
              {t('nav.modelSelectorDetailed.selectedModels')} ({selectedModels.length})
            </Text>
            <Text color="tertiary" size="xs">
              {t('nav.modelSelectorDetailed.maxLabel')} {effectiveMaxModels}
            </Text>
          </View>
          <View style={styles.selectedList}>
            {selectedModels.map(m => (
              <Pressable
                key={`sel-${m.id}`}
                style={styles.selectedChip}
                onPress={() => onRemoveModel(m.id)}
              >
                <Text size="n" color="primary" numberOfLines={1}>{m.name}</Text>
                <Icon name="xmark" size={12} color={colors.text_color_secondary} />
              </Pressable>
            ))}
          </View>
        </View>
      )}

      {isDoneVisible && (
        <View style={styles.doneContainer}>
          <Button
            text={t('nav.modelSelectorDetailed.done')}
            variant="primary"
            size="medium"
            onPress={onRequestClose}
            isFullWidth
          />
        </View>
      )}
    </View>
  );
};

export default ModelSelectorDetailedDesktop;
